/**
 * Utility functions for consistent segment display formatting across the application
 */

/**
 * Format segment location/route display based on segment type
 * @param {Object} segment - The segment object
 * @returns {string} - Formatted display string
 */
export const formatSegmentLocation = (segment) => {
  if (!segment) return "";

  const type = segment.type;
  const typeName = formatSegmentType(type);

  // Transportation types: show From > To
  const transportationTypes = ['flight', 'train', 'bus', 'cruise', 'car_rental', 'rideshare'];
  
  if (transportationTypes.includes(type)) {
    const from = segment.from_location || "";
    const to = segment.to_location || "";
    
    if (from && to) {
      return `${typeName}: ${from} > ${to}`;
    }
    return typeName;
  }

  // Accommodation, Site Plan, Activity, and Event: show Location
  if (type === 'accommodation' || type === 'site_plan' || type === 'activity' || type === 'event') {
    const location = segment.accommodation_location || "";
    if (location) {
      return `${typeName}: ${location}`;
    }
    return typeName;
  }

  return typeName;
};

/**
 * Format segment location with additional details when available
 * @param {Object} segment - The segment object
 * @returns {string} - Formatted display string with details
 */
export const formatSegmentLocationWithDetails = (segment) => {
  if (!segment) return "";

  const type = segment.type;
  const typeName = formatSegmentType(type);
  
  // Transportation types: show From > To with additional info
  const transportationTypes = ['flight', 'train', 'bus', 'cruise', 'car_rental', 'rideshare'];
  
  if (transportationTypes.includes(type)) {
    const from = segment.from_location || "";
    const to = segment.to_location || "";
    
    if (from && to) {
      let route = `${from} > ${to}`;
      
      // Add flight number for flights
      if (type === 'flight' && segment.flight_number) {
        return `${typeName} ${segment.flight_number}: ${route}`;
      }
      
      // Add vendor for other transport if available
      if (segment.vendor_name && type !== 'flight') {
        return `${typeName}: ${route}`;
      }
      
      return `${typeName}: ${route}`;
    }
    return typeName;
  }

  // Accommodation: show vendor/hotel name with location
  if (type === 'accommodation') {
    const location = segment.accommodation_location || "";
    if (location && segment.vendor_name) {
      return `${typeName}: ${segment.vendor_name} - ${location}`;
    }
    if (location) {
      return `${typeName}: ${location}`;
    }
    return typeName;
  }

  // Site Plan: show location
  if (type === 'site_plan') {
    const location = segment.accommodation_location || "";
    if (location) {
      return `${typeName}: ${location}`;
    }
    return typeName;
  }

  // Activity and Event: show location with country if available
  if (type === 'activity' || type === 'event') {
    const location = segment.accommodation_location || "";
    const country = segment.country || "";
    
    if (location && country) {
      return `${typeName}: ${location}, ${country}`;
    }
    if (location) {
      return `${typeName}: ${location}`;
    }
    return typeName;
  }

  return typeName;
};

/**
 * Format segment type for display
 * @param {string} type - The segment type
 * @returns {string} - Formatted type name
 */
export const formatSegmentType = (type) => {
  const typeMap = {
    'flight': 'Flight',
    'train': 'Train',
    'bus': 'Bus',
    'cruise': 'Cruise',
    'car_rental': 'Car Rental',
    'rideshare': 'Rideshare',
    'accommodation': 'Accommodation',
    'site_plan': 'Site Plan'
  };
  
  return typeMap[type] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Extract IATA codes from location string
 * @param {string} location - Location string (e.g., "Los Angeles (LAX)")
 * @returns {string} - IATA code or empty string
 */
export const extractIataCode = (location) => {
  if (!location) return "";
  const match = location.match(/\(([A-Z]{3})\)/);
  return match ? match[1] : "";
};

/**
 * Format compact location for display (IATA codes for airports, city names for accommodations)
 * @param {Object} segment - The segment object
 * @returns {string} - Compact formatted location
 */
export const formatCompactLocation = (segment) => {
  if (!segment) return "";

  const type = segment.type;
  const transportationTypes = ['flight', 'train', 'bus', 'cruise', 'car_rental', 'rideshare'];
  
  if (transportationTypes.includes(type)) {
    const from = segment.from_location || "";
    const to = segment.to_location || "";
    
    if (from && to) {
      // For flights, try to extract IATA codes
      if (type === 'flight') {
        const fromIata = extractIataCode(from) || from;
        const toIata = extractIataCode(to) || to;
        return `${fromIata} > ${toIata}`;
      }
      return `${from} > ${to}`;
    }
  }

  // Accommodation: show just the city/location
  if (type === 'accommodation' || type === 'site_plan') {
    return segment.accommodation_location || "";
  }

  return "";
};
